import React from 'react';
import { useSettings } from '@/components/settings/lib/settings-context';
import { formatHeight, formatWeight, convertHeightForDisplay, convertWeightForDisplay } from '@/components/clients/lib/unit-conversion';
import { Button } from './button';
import { cn } from '@/lib/utils';

interface UnitDisplayProps {
  value: number;
  type: 'height' | 'weight' | 'length' | 'distance';
  className?: string;
  showUnit?: boolean;
  precision?: number;
  onClick?: () => void;
}

export function UnitDisplay({ 
  value, 
  type, 
  className, 
  showUnit = true, 
  precision = 1,
  onClick 
}: UnitDisplayProps) {
  const { settings, setUnit } = useSettings();
  
  const handleUnitClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Toggle between metric and imperial
      const newUnit = settings.unit === 'cm' ? 'inches' : 'cm';
      setUnit(newUnit);
    }
  };

  const getDisplayValue = () => {
    switch (type) {
      case 'height':
        return convertHeightForDisplay(value, settings.unit);
      case 'weight':
        return convertWeightForDisplay(value, settings.unit);
      case 'length':
      case 'distance':
        return convertHeightForDisplay(value, settings.unit);
      default:
        return value;
    }
  };

  const getUnitLabel = () => {
    switch (type) {
      case 'height':
        return settings.unit === 'inches' ? 'in' : 'cm';
      case 'weight':
        return settings.unit === 'inches' ? 'lbs' : 'kg';
      case 'length':
      case 'distance':
        return settings.unit === 'inches' ? 'in' : 'cm';
      default:
        return '';
    }
  };

  const displayValue = getDisplayValue();
  const unitLabel = getUnitLabel();
  const formattedValue = precision > 0 ? displayValue.toFixed(precision) : Math.round(displayValue);

  if (!showUnit) {
    return <span className={className}>{formattedValue}</span>;
  }

  return (
    <div className={cn("inline-flex items-center gap-1", className)}>
      <span>{formattedValue}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-auto p-0 text-xs font-normal text-muted-foreground hover:text-foreground"
        onClick={handleUnitClick}
      >
        {unitLabel}
      </Button>
    </div>
  );
}

// Specialized components for common use cases
export function HeightDisplay({ value, className, precision = 0 }: Omit<UnitDisplayProps, 'type'>) {
  return (
    <UnitDisplay 
      value={value} 
      type="height" 
      className={className} 
      precision={precision}
    />
  );
}

export function WeightDisplay({ value, className, precision = 1 }: Omit<UnitDisplayProps, 'type'>) {
  return (
    <UnitDisplay 
      value={value} 
      type="weight" 
      className={className} 
      precision={precision}
    />
  );
}

export function LengthDisplay({ value, className, precision = 1 }: Omit<UnitDisplayProps, 'type'>) {
  return (
    <UnitDisplay 
      value={value} 
      type="length" 
      className={className} 
      precision={precision}
    />
  );
}
